import { IProductCard } from '@src/components/product-card/product-card.types'
import { Document } from '@src/quince-site-common/src/constants/contentfulRichTextTypes'
import { PRODUCT_SORT_VALUES } from '@src/quince-site-common/src/constants/product'
import Button from '@src/quince-site-common/src/domains/Button'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import Directory from '@src/quince-site-common/src/domains/Directory'
import Image from '@src/quince-site-common/src/domains/Image'
import RichText from '@src/quince-site-common/src/domains/RichText'
import appData from '@src/quince-site-common/src/helpers/appData'

import ContentfulCollectionMedia from '../types/ContentfulCollectionMedia'
import { IContentfulCollection } from '../types/ContentfulCollectionService.types'

import { Filter, SelectedFilter } from './CollectionFilter'

export enum COLLECTION_TYPE {
  BEST_SELLER = 'BEST_SELLER',
  UNKNOWN = 'UNKNOWN',
  DEFAULT = 'DEFAULT',
}

export enum COLLECTION_CONTENT_TYPE {
  WITH_PAGINATION = 'WITH_PAGINATION',
}

export enum COLLECTION_CATEGORY {
  PICKED_FOR_YOU = 'PICKED_FOR_YOU',
}

export enum COLLECTION_PAGE_TYPE {
  STATIC = 'STATIC',
  BUNDLE = 'BUNDLE',
}

interface ICollection {
  readonly title: string
  readonly contentful_id: string
  readonly slug: string
  readonly subCollections: (SubCollection | CustomSection)[]
  readonly subCollectionCards: SubCollection[]
  readonly totalCount?: number
  readonly subCollectionsV2?: (SubCollection | CustomSection)[]
  readonly totalCountV2?: number
  readonly filters: Filter[]
  readonly subcategoriesButtonsDesktop?: Button[]
  readonly subcategoriesButtonsMobile?: Button[]
  readonly subcategoriesLinkMobile?: IContentfulCollection[] | undefined
  readonly subcategoriesLinkDesktop?: IContentfulCollection[] | undefined
  defaultSubCollectionOrder?: (string | number)[]
  readonly live: boolean | null
  readonly noIndex?: boolean
  readonly collectionPageType?: COLLECTION_PAGE_TYPE
  readonly collectionType: string
  readonly collectionCategory?: COLLECTION_CATEGORY
  nextFeedURL?: string
  nextFeedURLV2?: string
  readonly additionalAttributes?: Record<string, string>
  readonly collectionContentType?: COLLECTION_CONTENT_TYPE
  readonly selectedFilter?: SelectedFilter
  readonly sortBy?: PRODUCT_SORT_VALUES
  readonly collectionBannerMobile?: CustomSection
  readonly collectionBannerDesktop?: CustomSection
  readonly collectionExploreEverythingImage?: ContentfulCollectionMedia
  readonly breadCrumbs: Directory
  readonly offerMessage?: Document
  readonly subcollectionSortingType?: string
}

export default class Collection implements ICollection {
  readonly title: string
  readonly contentful_id: string
  slug: string
  readonly subCollections: (SubCollection | CustomSection)[]
  readonly subCollectionCards: SubCollection[]
  readonly totalCount?: number
  readonly subCollectionsV2?: (SubCollection | CustomSection)[]
  readonly totalCountV2?: number
  readonly filters: Filter[]
  readonly subcategoriesButtonsDesktop?: Button[]
  readonly subcategoriesButtonsMobile?: Button[]
  readonly subcategoriesLinkMobile?: IContentfulCollection[] | undefined
  readonly subcategoriesLinkDesktop?: IContentfulCollection[] | undefined
  readonly live: boolean | null
  readonly noIndex?: boolean
  readonly collectionPageType?: COLLECTION_PAGE_TYPE
  collectionType: string
  collectionCategory?: COLLECTION_CATEGORY
  experimentCollection?: Collection
  defaultSubCollectionOrder?: (string | number)[]
  nextFeedURL?: string
  nextFeedURLV2?: string
  readonly additionalAttributes?: Record<string, string>
  readonly collectionContentType?: COLLECTION_CONTENT_TYPE
  readonly selectedFilter?: SelectedFilter
  readonly sortBy: PRODUCT_SORT_VALUES
  readonly collectionBannerMobile?: CustomSection
  readonly collectionBannerDesktop?: CustomSection
  readonly collectionExploreEverythingImage?: ContentfulCollectionMedia
  readonly breadCrumbs: Directory
  readonly offerMessage?: Document
  readonly subcollectionSortingType?: string

  constructor({
    title,
    contentful_id,
    subCollections,
    subCollectionCards,
    totalCount,
    filters,
    slug,
    subcategoriesButtonsMobile,
    subcategoriesButtonsDesktop,
    subcategoriesLinkMobile,
    subcategoriesLinkDesktop,
    defaultSubCollectionOrder,
    live,
    noIndex,
    collectionPageType,
    collectionType,
    collectionCategory,
    nextFeedURL,
    nextFeedURLV2,
    additionalAttributes,
    collectionContentType,
    subCollectionsV2,
    totalCountV2,
    selectedFilter,
    sortBy = PRODUCT_SORT_VALUES.FEATURED,
    collectionBannerMobile,
    collectionBannerDesktop,
    collectionExploreEverythingImage,
    breadCrumbs,
    offerMessage,
    subcollectionSortingType,
  }: ICollection) {
    this.title = title
    this.contentful_id = contentful_id
    this.subCollections = subCollections
    this.subCollectionCards = subCollectionCards
    this.totalCount = totalCount
    this.subCollectionsV2 = subCollectionsV2
    this.totalCountV2 = totalCountV2
    this.filters = filters
    this.slug = slug
    this.subcategoriesButtonsMobile = appData.shouldHaveMobileData ? subcategoriesButtonsMobile : undefined
    this.subcategoriesButtonsDesktop = appData.shouldHaveDesktopData ? subcategoriesButtonsDesktop : undefined
    this.subcategoriesLinkMobile = appData.shouldHaveMobileData ? subcategoriesLinkMobile : undefined
    this.subcategoriesLinkDesktop = appData.shouldHaveDesktopData ? subcategoriesLinkDesktop : undefined
    this.defaultSubCollectionOrder = defaultSubCollectionOrder
    this.live = live
    this.noIndex = noIndex
    this.collectionPageType = collectionPageType
    this.collectionType = collectionType
    this.collectionCategory = collectionCategory
    this.nextFeedURL = nextFeedURL
    this.nextFeedURLV2 = nextFeedURLV2
    this.additionalAttributes = additionalAttributes
    this.collectionContentType = collectionContentType
    this.selectedFilter = selectedFilter
    this.sortBy = sortBy
    this.collectionBannerMobile = collectionBannerMobile
    this.collectionBannerDesktop = collectionBannerDesktop
    this.collectionExploreEverythingImage = collectionExploreEverythingImage
    this.breadCrumbs = breadCrumbs
    this.offerMessage = offerMessage
    this.subcollectionSortingType = subcollectionSortingType
    return {
      ...this,
    }
  }
}

export interface ISubCollection {
  readonly id: string | number
  readonly seoTitle?: string
  readonly title?: string
  readonly displayTitle?: string
  readonly subTitle?: RichText
  readonly showAllColors?: boolean | undefined
  readonly noIndex?: boolean | undefined
  productCards?: IProductCard[]
  readonly marketingPanelBase?: CustomSection
  readonly marketingPanelMobile?: CustomSection
  readonly faqSection?: CustomSection
  readonly imageDesktop?: Image
  slug?: string
  readonly additionalAttributes?: Record<string, string>
  readonly navigationId?: string
  revSortedIdColor?: string[]
  totalProducts?: number
}

const SUB_COLLECTION_TYPE = 'SubCollection'
export class SubCollection implements ISubCollection {
  readonly id: string | number
  readonly seoTitle?: string
  readonly title?: string
  readonly displayTitle?: string
  readonly subTitle?: RichText
  readonly showAllColors: boolean | undefined
  readonly noIndex?: boolean
  productCards?: IProductCard[]
  readonly marketingPanelBase?: CustomSection
  readonly marketingPanelMobile?: CustomSection
  readonly faqSection?: CustomSection
  typename = SUB_COLLECTION_TYPE
  readonly imageDesktop?: Image
  slug?: string
  readonly additionalAttributes?: Record<string, string>
  readonly navigationId?: string
  revSortedIdColor?: string[]
  totalProducts?: number

  constructor({
    id,
    title,
    displayTitle,
    seoTitle,
    subTitle,
    showAllColors,
    noIndex,
    productCards,
    marketingPanelBase,
    marketingPanelMobile,
    faqSection,
    imageDesktop,
    slug,
    additionalAttributes,
    navigationId,
    revSortedIdColor,
    totalProducts,
  }: ISubCollection) {
    this.id = id
    this.seoTitle = seoTitle
    this.title = title
    this.displayTitle = displayTitle
    this.subTitle = subTitle
    this.showAllColors = showAllColors
    this.noIndex = noIndex
    this.productCards = productCards
    this.totalProducts = totalProducts
    this.marketingPanelBase = appData.shouldHaveDesktopData ? marketingPanelBase : undefined
    this.marketingPanelMobile = appData.shouldHaveMobileData ? marketingPanelMobile : undefined
    this.faqSection = faqSection
    this.imageDesktop = appData.shouldHaveDesktopData ? imageDesktop : undefined
    this.slug = slug
    this.additionalAttributes = additionalAttributes
    this.navigationId = navigationId
    this.revSortedIdColor = revSortedIdColor

    return {
      ...this,
    }
  }
}

export { ECollectionType } from '../services/collectionService/CollectionService.types'
